import { Color } from './java/Color';
import { Graphics2D, Font, FontMetrics, RenderingHints } from './java/Graphics2D';
import { Rectangle } from './java/Geom';
import { Random } from './java/Random';
import { GameObject } from './GameObject';
import { Handler } from './Handler';
import { ID } from './ID';
import { Game } from './Game';
import { SwarmMinion } from './SwarmMinion';
import { PageRenderer } from './PageRenderer';

/**
 * Swarm Boss — relatively fragile but summons waves of mini-enemies.
 * Drifts slowly around the arena. Periodically spawns SwarmMinions that bounce
 * and shoot small bullets. The boss itself doesn't attack directly.
 * Defeat it fast before the minions overwhelm you.
 */
export class BossSwarm extends GameObject {

  private static readonly SIZE = 72;

  private handler: Handler;
  private r: Random = new Random();
  private pulsePhase = 0;
  private readonly boundsRect: Rectangle = new Rectangle();
  private static readonly EMPTY_BOUNDS: Rectangle = new Rectangle(0, 0, 0, 0);

  // Entry
  private entryTimer = 100;
  private warmupTimer = 30;

  // Health — drains faster than other bosses (fragile)
  private maxHp = 800;
  private hp: number;
  private defeated = false;
  private deathTimer = 0;

  // Summon system
  private summonCooldown = 0;
  private summonWave = 0;

  // Movement — slow drift
  private driftAngle: number;

  // Colors — amber/orange theme
  private static readonly FILL_P1: Color = new Color(245, 180, 60);
  private static readonly FILL_P2: Color = new Color(240, 140, 50);
  private static readonly FILL_P3: Color = new Color(235, 100, 50);
  private static readonly BAR_BG: Color = new Color(22, 30, 44);
  private static readonly BAR_BORDER: Color = new Color(40, 52, 70);
  private static readonly FONT_BOSS: Font = new Font('Arial', Font.BOLD, 14);
  private static readonly FONT_PHASE: Font = new Font('Arial', Font.BOLD, 11);

  // Minion tracking
  private static readonly MAX_MINIONS = 8;

  constructor(x: number, y: number, id: ID, handler: Handler) {
    super(x, y, id);
    this.handler = handler;
    this.hp = this.maxHp;
    this.driftAngle = this.r.nextFloat() * (Math.PI * 2);
    this.velX = 0;
    this.velY = 2;
  }

  setMaxHp(hp: number): void {
    this.maxHp = hp;
    this.hp = hp;
  }

  isDefeated(): boolean {
    return this.defeated && this.deathTimer > 60;
  }

  takeDamage(dmg: number): boolean {
    if (this.defeated) return false;
    this.hp -= dmg;
    if (this.hp < 0) this.hp = 0;
    return true;
  }

  getHpPercent(): number { return this.hp / this.maxHp; }

  getPhase(): number {
    const pct = this.hp / this.maxHp;
    if (pct <= 0.25) return 3;
    if (pct <= 0.55) return 2;
    return 1;
  }

  getBounds(): Rectangle {
    if (this.defeated || this.entryTimer > 0) return BossSwarm.EMPTY_BOUNDS;
    this.boundsRect.x = Math.trunc(this.x);
    this.boundsRect.y = Math.trunc(this.y);
    this.boundsRect.width = BossSwarm.SIZE;
    this.boundsRect.height = BossSwarm.SIZE;
    return this.boundsRect;
  }

  tick(): void {
    const SIZE = BossSwarm.SIZE;

    if (this.defeated) {
      this.deathTimer++;
      // Kill all minions when boss dies
      if (this.deathTimer === 1) {
        for (let i = this.handler.getObjects().length - 1; i >= 0; i--) {
          const obj = this.handler.getObjects()[i];
          if (obj instanceof SwarmMinion) {
            this.handler.removeObject(obj);
          }
        }
        Game.triggerBulletCascade(this.x + Math.trunc(SIZE / 2), this.y + Math.trunc(SIZE / 2), this.handler);
      }
      return;
    }

    this.pulsePhase += 0.07;

    // Entry animation
    if (this.entryTimer > 0) {
      this.x += this.velX;
      this.y += this.velY;
      this.entryTimer--;
      if (this.entryTimer <= 0) {
        this.velX = 0;
        this.velY = 0;
      }
      return;
    }
    if (this.warmupTimer > 0) {
      this.warmupTimer--;
      return;
    }

    // HP drains faster than other bosses
    let drainRate = 1.0 + (1 - this.hp / this.maxHp) * 0.5;
    if (Game.combatMode) drainRate *= 0.35;
    this.hp -= drainRate;

    if (this.hp <= 0) {
      this.hp = 0;
      this.defeated = true;
      return;
    }

    // Slow drift movement
    this.driftAngle += 0.01 + this.r.nextFloat() * 0.005;
    const speed = 1.2;
    this.x += Math.cos(this.driftAngle) * speed;
    this.y += Math.sin(this.driftAngle) * speed * 0.6;

    // Keep in bounds with soft bounce
    const margin = 60;
    if (this.x < margin) { this.x = margin; this.driftAngle = Math.PI - this.driftAngle; }
    if (this.x > Game.WIDTH - SIZE - margin) { this.x = Game.WIDTH - SIZE - margin; this.driftAngle = Math.PI - this.driftAngle; }
    if (this.y < margin) { this.y = margin; this.driftAngle = -this.driftAngle; }
    if (this.y > Game.HEIGHT - SIZE - margin) { this.y = Game.HEIGHT - SIZE - margin; this.driftAngle = -this.driftAngle; }

    // Summon minions
    this.summonCooldown--;
    if (this.summonCooldown <= 0) {
      const phase = this.getPhase();
      const minionCount = this.countMinions();

      if (minionCount < BossSwarm.MAX_MINIONS) {
        let toSpawn = phase === 3 ? 3 : phase === 2 ? 2 : 1;
        toSpawn = Math.min(toSpawn, BossSwarm.MAX_MINIONS - minionCount);
        this.summonWave++;

        for (let i = 0; i < toSpawn; i++) {
          const angle = (Math.PI * 2 * i / toSpawn) + this.summonWave * 1.2;
          const dist = SIZE * 0.8;
          let sx = Math.trunc(this.x + Math.trunc(SIZE / 2) + Math.cos(angle) * dist);
          let sy = Math.trunc(this.y + Math.trunc(SIZE / 2) + Math.sin(angle) * dist);
          sx = Math.max(20, Math.min(Game.WIDTH - 40, sx));
          sy = Math.max(20, Math.min(Game.HEIGHT - 40, sy));

          const shoots = phase >= 2 || (this.summonWave % 3 === 0);
          this.handler.addObject(new SwarmMinion(sx, sy, this.handler, shoots));
        }
      }

      this.summonCooldown = phase === 3 ? 100 : phase === 2 ? 140 : 180;
    }
  }

  private countMinions(): number {
    let count = 0;
    for (let i = 0; i < this.handler.getObjects().length; i++) {
      if (this.handler.getObjects()[i] instanceof SwarmMinion) count++;
    }
    return count;
  }

  render(g: Graphics2D): void {
    const SIZE = BossSwarm.SIZE;
    const g2 = g;
    g2.setRenderingHint(RenderingHints.KEY_ANTIALIASING, RenderingHints.VALUE_ANTIALIAS_ON);
    const ix = Math.trunc(this.x), iy = Math.trunc(this.y);

    if (this.defeated) {
      const t = Math.min(this.deathTimer / 60, 1);
      const shrink = Math.trunc(t * SIZE / 2);
      const alpha = Math.trunc((1 - t) * 255);
      if (alpha > 0) {
        g2.setColor(new Color(255, 255, 255, alpha));
        g2.fillOval(ix + shrink, iy + shrink, SIZE - shrink * 2, SIZE - shrink * 2);
      }
      return;
    }

    const fill = this.getPhase() === 3 ? BossSwarm.FILL_P3 : this.getPhase() === 2 ? BossSwarm.FILL_P2 : BossSwarm.FILL_P1;
    const pulse = Math.sin(this.pulsePhase) * 0.5 + 0.5;
    const cx = ix + Math.trunc(SIZE / 2), cy = iy + Math.trunc(SIZE / 2);

    // Glow
    const glowSize = 10 + Math.trunc(5 * pulse);
    g2.setColor(new Color(fill.getRed(), fill.getGreen(), fill.getBlue(), 12 + Math.trunc(10 * pulse)));
    g2.fillOval(cx - Math.trunc(SIZE / 2) - glowSize, cy - Math.trunc(SIZE / 2) - glowSize,
      SIZE + glowSize * 2, SIZE + glowSize * 2);

    // Main body — pentagon shape
    const hr = Math.trunc(SIZE / 2);
    const hx: number[] = new Array<number>(5).fill(0), hy: number[] = new Array<number>(5).fill(0);
    for (let i = 0; i < 5; i++) {
      const a = this.pulsePhase * 0.2 + i * (Math.PI * 2 / 5) - (Math.PI / 2);
      hx[i] = cx + Math.trunc(Math.cos(a) * hr);
      hy[i] = cy + Math.trunc(Math.sin(a) * hr);
    }
    g2.setColor(fill);
    g2.fillPolygon(hx, hy, 5);
    g2.setColor(new Color(fill.getRed(), fill.getGreen(), fill.getBlue(), 100 + Math.trunc(60 * pulse)));
    g2.drawPolygon(hx, hy, 5);

    // Inner star pattern
    g2.setColor(new Color(255, 255, 255, 25 + Math.trunc(15 * pulse)));
    const innerR = Math.trunc(SIZE / 4);
    for (let i = 0; i < 5; i++) {
      const a1 = this.pulsePhase * 0.2 + i * (Math.PI * 2 / 5) - (Math.PI / 2);
      const a2 = this.pulsePhase * 0.2 + ((i + 2) % 5) * (Math.PI * 2 / 5) - (Math.PI / 2);
      g2.drawLine(
        cx + Math.trunc(Math.cos(a1) * innerR), cy + Math.trunc(Math.sin(a1) * innerR),
        cx + Math.trunc(Math.cos(a2) * innerR), cy + Math.trunc(Math.sin(a2) * innerR));
    }

    // Orbiting summon indicators
    const minionCount = this.countMinions();
    if (minionCount > 0) {
      g2.setColor(new Color(fill.getRed(), fill.getGreen(), fill.getBlue(), 50));
      for (let i = 0; i < minionCount; i++) {
        const a = this.pulsePhase * 0.6 + i * (Math.PI * 2 / minionCount);
        const dx = cx + Math.trunc(Math.cos(a) * (Math.trunc(SIZE / 2) + 12));
        const dy = cy + Math.trunc(Math.sin(a) * (Math.trunc(SIZE / 2) + 12));
        g2.fillOval(dx - 3, dy - 3, 6, 6);
      }
    }

    this.renderBossBar(g2, minionCount);
  }

  private renderBossBar(g: Graphics2D, minionCount: number): void {
    const barW = 400;
    const barH = 10;
    const barX = Math.trunc((Game.WIDTH - barW) / 2);
    const barY = Game.HEIGHT - 40;
    const pct = this.hp / this.maxHp;

    g.setColor(BossSwarm.BAR_BG);
    g.fillRoundRect(barX, barY, barW, barH, 5, 5);

    const fill = this.getPhase() === 3 ? BossSwarm.FILL_P3 : this.getPhase() === 2 ? BossSwarm.FILL_P2 : BossSwarm.FILL_P1;
    const fillW = Math.trunc(barW * pct);
    if (fillW > 0) {
      g.setColor(fill);
      g.fillRoundRect(barX, barY, fillW, barH, 5, 5);
    }

    g.setColor(BossSwarm.BAR_BORDER);
    g.drawRoundRect(barX, barY, barW, barH, 5, 5);

    g.setFont(BossSwarm.FONT_BOSS);
    g.setColor(PageRenderer.TEXT_SEC);
    const fm: FontMetrics = g.getFontMetrics();
    g.drawString('SWARM', barX - fm.stringWidth('SWARM') - 10, barY + 9);

    g.setFont(BossSwarm.FONT_PHASE);
    g.setColor(fill);
    g.drawString(minionCount + ' minions', barX + barW + 10, barY + 9);
  }
}
